// trefferUmfeld.ts — Unternehmensverbund bzw. Haushalt des gefundenen Kontakts
// als gewöhnliche Tabellenzeilen (User 28.09.2026: „das muss schon in der
// Tabelle mit angezeigt werden als Elemente"). Vorher stand das Umfeld als
// eigene Karte UNTER der Tabelle.
//
// Ablauf: `trefferKandidat` (unternehmensverbund.ts) wählt den Kontakt, der
// Server liefert Verbund bzw. Haushalt, daraus wird hier ein {@link UmfeldPlan}
// (Gruppen mit Rolle je Kontakt). Die vollen Zeilen holt die Tabelle über die
// Kontaktliste (`filters.Ids`) und {@link trefferUndUmfeld} setzt beides
// zusammen: Treffer oben, das Umfeld als eigener Abschnitt dahinter — jeder
// Kontakt nur einmal, und zwar im Umfeld (dort steht seine Rolle).
//
// Bewusst ohne React, damit die Regeln per Vitest prüfbar sind.

import {
  beziehungText,
  hatInhalt,
  rollenTeile,
  type Unternehmensverbund,
} from "./unternehmensverbund";
import { beziehungDesMitglieds, hatHaushalt, lebensdatenText, type Haushalt } from "./haushalt";

export type UmfeldRollenTeil = { text: string; ausgeschieden: boolean; ende: string };

/** Was unter dem Namen einer Umfeld-Zeile steht. */
export type UmfeldRolle = {
  teile: UmfeldRollenTeil[];
  /** Alle Rollen beendet → Name durchgestrichen. */
  ausgeschieden: boolean;
};

export type UmfeldGruppe = {
  key: string;
  titel: string;
  eintraege: Array<{ kontaktId: number; rolle: UmfeldRolle }>;
};

export type UmfeldPlan = {
  art: "firma" | "person";
  /** Der gefundene Kontakt selbst — er bleibt bei den Treffern. */
  startId: number;
  /** „Unternehmensverbund von" / „Haushalt von" */
  kopf: string;
  name: string;
  gruppen: UmfeldGruppe[];
};

const einfacheRolle = (text: string): UmfeldRolle => ({
  teile: text ? [{ text, ausgeschieden: false, ende: "" }] : [],
  ausgeschieden: false,
});

/**
 * Gruppen bereinigen: der Startkontakt fliegt raus, jeder Kontakt steht nur in
 * der ersten Gruppe, in der er vorkommt; leere Gruppen entfallen. Steht er in
 * einer späteren Gruppe noch einmal (Leitung UND Gesellschafter), wandern
 * deren Rollen in seine Zeile — wie in der früheren Verbund-Karte stehen dann
 * alle Rollen nebeneinander. Ausgeschieden ist er nur, wenn ALLE Rollen enden.
 */
function bereinigt(startId: number, gruppen: UmfeldGruppe[]): UmfeldGruppe[] {
  const erste = new Map<number, { kontaktId: number; rolle: UmfeldRolle }>();
  const ergebnis = gruppen.map((g) => ({
    ...g,
    eintraege: g.eintraege.flatMap((e) => {
      if (e.kontaktId === startId) return [];
      const vorhanden = erste.get(e.kontaktId);
      if (vorhanden) {
        const texte = new Set(vorhanden.rolle.teile.map((t) => t.text));
        vorhanden.rolle = {
          teile: [...vorhanden.rolle.teile, ...e.rolle.teile.filter((t) => !texte.has(t.text))],
          ausgeschieden: vorhanden.rolle.ausgeschieden && e.rolle.ausgeschieden,
        };
        return [];
      }
      const kopie = { kontaktId: e.kontaktId, rolle: { ...e.rolle, teile: [...e.rolle.teile] } };
      erste.set(e.kontaktId, kopie);
      return [kopie];
    }),
  }));
  return ergebnis.filter((g) => g.eintraege.length > 0);
}

/** Plan aus dem Unternehmensverbund; `null`, wenn es nichts zu zeigen gibt. */
export function verbundPlan(v: Unternehmensverbund | null): UmfeldPlan | null {
  if (!hatInhalt(v) || !v.firma) return null;
  const startId = v.firma.id;
  const inhaber = (
    key: string,
    titel: string,
    liste: Unternehmensverbund["leitung"],
    opts: { verantwortlichkeit?: boolean } = {},
  ): UmfeldGruppe => ({
    key,
    titel,
    eintraege: liste.map((e) => ({
      kontaktId: e.id,
      rolle: { teile: rollenTeile(e, startId, opts), ausgeschieden: !!e.ausgeschieden },
    })),
  });
  const gruppen = bereinigt(startId, [
    {
      key: "firmen",
      titel: "Firmen im Verbund",
      eintraege: v.firmen.map((f) => ({ kontaktId: f.id, rolle: einfacheRolle(beziehungText(f.beziehung)) })),
    },
    inhaber("leitung", "Unternehmensleitung", v.leitung),
    inhaber("gesellschafter", "Gesellschafter", v.gesellschafter),
    // Unter dem Namen steht die Verantwortlichkeit („Steuerberater").
    inhaber("verantwortung", "Verantwortung fürs Unternehmen", v.verantwortung ?? [], { verantwortlichkeit: true }),
  ]);
  if (!gruppen.length) return null;
  return { art: "firma", startId, kopf: "Unternehmensverbund von", name: v.firma.name ?? "dieser Firma", gruppen };
}

/**
 * Plan aus dem Haushalt; `null`, wenn es nichts zu zeigen gibt. Unter dem
 * Namen stehen Beziehung und Lebensdaten — der Status hat seine eigene Spalte.
 */
export function haushaltPlan(h: Haushalt | null, heute: Date = new Date()): UmfeldPlan | null {
  if (!hatHaushalt(h) || !h.person) return null;
  const startId = h.person.id;
  const gruppen = bereinigt(startId, [
    {
      key: "haushalt",
      titel: "Haushaltsmitglieder",
      eintraege: h.mitglieder.map((m) => ({
        kontaktId: m.id,
        rolle: einfacheRolle(
          [beziehungDesMitglieds(m), lebensdatenText(m, heute)].map((s) => s.trim()).filter(Boolean).join(" · "),
        ),
      })),
    },
  ]);
  if (!gruppen.length) return null;
  return { art: "person", startId, kopf: "Haushalt von", name: h.person.name ?? "dieser Person", gruppen };
}

/** Die Ids, deren Zeilen die Tabelle nachladen muss (in Plan-Reihenfolge). */
export function umfeldIds(plan: UmfeldPlan | null): number[] {
  return plan ? plan.gruppen.flatMap((g) => g.eintraege.map((e) => e.kontaktId)) : [];
}

export type UmfeldZeile<R> = R & { umfeldRolle: UmfeldRolle };

/**
 * Treffer und Umfeld zusammensetzen.
 *
 *  - Eine Umfeld-Zeile kommt aus den nachgeladenen Zeilen, ersatzweise aus den
 *    Treffern (dieselbe Zeile, schon da). Fehlt sie in beiden — der Anwender
 *    sieht den Kontakt in der Liste nicht —, entfällt der Eintrag.
 *  - Ein Treffer, der im Umfeld steht, verschwindet oben: jeder Kontakt nur
 *    einmal, und zwar dort, wo seine Rolle steht.
 *  - Bleibt vom Umfeld nichts übrig, bleiben die Treffer unverändert.
 */
export function trefferUndUmfeld<R extends { kontaktId: number }>(
  treffer: R[],
  plan: UmfeldPlan | null,
  umfeldZeilen: R[],
): { treffer: R[]; gruppen: Array<{ key: string; titel: string; zeilen: UmfeldZeile<R>[] }> } {
  if (!plan) return { treffer, gruppen: [] };
  const quelle = new Map<number, R>();
  for (const r of treffer) quelle.set(r.kontaktId, r);
  for (const r of umfeldZeilen) quelle.set(r.kontaktId, r);

  const imUmfeld = new Set<number>();
  const gruppen = plan.gruppen
    .map((g) => ({
      key: g.key,
      titel: g.titel,
      zeilen: g.eintraege.flatMap((e) => {
        const r = quelle.get(e.kontaktId);
        if (!r || e.kontaktId === plan.startId || imUmfeld.has(e.kontaktId)) return [];
        imUmfeld.add(e.kontaktId);
        return [{ ...r, umfeldRolle: e.rolle }];
      }),
    }))
    .filter((g) => g.zeilen.length > 0);

  if (!gruppen.length) return { treffer, gruppen: [] };
  return { treffer: treffer.filter((r) => !imUmfeld.has(r.kontaktId)), gruppen };
}
